import type { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { AnalyticsMode } from '@recruitee/analytics';
import { CandidateAnalytics } from '@recruitee/candidate-util';
import { DisqualifyReasonsDropdownComponent } from '@recruitee/disqualify-reasons';
import type { DisqualifyReason } from '@recruitee/disqualify-reasons-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { Placement } from '@recruitee/placements-types';
import { RequisitionOpeningIssue } from '@recruitee/requisitions-types';
import type { Stage } from '@recruitee/stages-types';
import { StageGroup } from '@recruitee/stages-types';
import { ButtonComponent, IconComponent, RtTooltip } from '@recruitee/user-interface';
import { WalkthroughStepOriginDirective } from '@recruitee/walkthrough';

import type {
  DisqualifyEvent,
  PlacementActions,
  PlacementEvent,
  ProceedEvent,
} from '../../candidate-profile-placements/types';
import { getNextStage } from '../../candidate-profile-placements/utils';

@Component({
  selector: 'rt-action-buttons',
  templateUrl: './action-buttons.component.html',
  styleUrls: ['./action-buttons.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    IconComponent,
    TranslatePipe,
    RtTooltip,
    WalkthroughStepOriginDirective,
    DisqualifyReasonsDropdownComponent,
  ],
})
export class ActionButtonsComponent {
  public readonly RequisitionOpeningIssue = RequisitionOpeningIssue;

  public readonly disqualifyDropdownPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  public placement = input.required<Placement>();

  public placementActions = input.required<PlacementActions>();

  public stages = input.required<Stage[]>();

  public disqualifyReasons = input.required<DisqualifyReason[]>();

  public isLoading = input<boolean>(false);

  public openingIssue = input<RequisitionOpeningIssue>();

  public isLastStage = computed(() => !this.nextStage());

  public showProceedAndDisqualifyButtons = computed(() => {
    if (this.placement().hiredInThisPlacement && this.isLastStage()) {
      return false;
    }

    return this.placementActions().disqualify || this.placementActions().proceed;
  });

  public nextStage = computed(() => getNextStage(this.placement(), this.stages()));

  public isProceedButtonDisabled = computed(() => {
    if (this.isLoading()) {
      return true;
    }

    if (!this.nextStage()) {
      return true;
    }

    return this.nextStage()!.group === StageGroup.Hires && !this.placementActions().hire;
  });

  public proceedButtonTooltip = computed(() => {
    if (!this.nextStage()) {
      return '';
    }

    if (this.nextStage()!.group === StageGroup.Hires && !this.placementActions().hire) {
      return this.i18n.translate('app.stages.no_hires_permission');
    }

    if (this.placementActions().proceed) {
      return this.i18n.translate('app.candidate.move_to') + ' ' + this.nextStage()?.name;
    }

    return '';
  });

  public proceedButtonTooltipPosition = computed(() =>
    this.isNextStageHired() ? 'below' : 'above',
  );

  public isNextStageHired = computed(
    () =>
      this.placement().stage.group !== StageGroup.Hires &&
      this.nextStage()?.group === StageGroup.Hires,
  );

  public proceedClick = output<ProceedEvent>();

  public editHiringDetailsClick = output<PlacementEvent>();

  public requalifyClick = output<PlacementEvent>();

  public disqualifyReasonSelect = output<DisqualifyEvent>();

  public editDisqualifyReasonsClick = output<void>();

  public disqualifyReasonsActionsDetails = output<DisqualifyReason>();

  constructor(
    private i18n: I18nService,
    private candidateAnalytics: CandidateAnalytics,
  ) {}

  public onProceedClick($event: MouseEvent): void {
    if (this.isProceedButtonDisabled()) {
      $event.stopImmediatePropagation();
      return;
    }

    const nextStage = this.nextStage();

    if (!nextStage) {
      return;
    }

    this.proceedClick.emit({
      placement: this.placement(),
      targetStage: nextStage,
      originStage: this.placement().stage,
      eventOrigin: 'proceed-click',
    });
  }

  public disqualifyReasonClick(disqualifyReason: DisqualifyReason): void {
    this.candidateAnalytics.trackDisqualify(AnalyticsMode.Single);
    this.disqualifyReasonSelect.emit({ placement: this.placement(), disqualifyReason });
  }

  public onRequalifyClick(): void {
    this.candidateAnalytics.trackRequalify(AnalyticsMode.Single);
    this.requalifyClick.emit({ placement: this.placement() });
  }
}
